import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { MessageSquare, Mail, Send, CheckCircle2, Phone, User, Clock, SendHorizonal, Route, Plus, X } from "lucide-react";

const typeColors = {
  fin_tournee: "bg-purple-100 text-purple-700 border-purple-200",
  j1: "bg-indigo-100 text-indigo-700 border-indigo-200",
  j7: "bg-amber-100 text-amber-700 border-amber-200",
  j30: "bg-emerald-100 text-emerald-700 border-emerald-200",
};

const SmsCard = ({ item, onSent, markEndpoint = "sms-mark-sent", typeField = "type_sms" }) => {
  const { token } = useAuth();
  const [message, setMessage] = useState(item.message);
  const [sending, setSending] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const handleSend = async () => {
    setSending(true);
    const body = encodeURIComponent(message);
    const phone = (item.client_telephone || "").replace(/\s+/g, "");
    window.location.href = `sms:${phone}?body=${body}`;
    setTimeout(async () => {
      try {
        await axios.post(`${API}/communications/${markEndpoint}`, {
          livraison_id: item.livraison_id,
          [typeField]: item.type,
        }, { headers });
        toast.success("Marqué comme envoyé");
        onSent(item.id);
      } catch { toast.error("Erreur"); }
      finally { setSending(false); }
    }, 500);
  };

  const markOnly = async () => {
    try {
      await axios.post(`${API}/communications/${markEndpoint}`, {
        livraison_id: item.livraison_id,
        [typeField]: item.type,
      }, { headers });
      toast.success("Marqué comme envoyé (sans ouvrir)");
      onSent(item.id);
    } catch { toast.error("Erreur"); }
  };

  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-white" data-testid={`sms-item-${item.id}`}>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2 text-sm min-w-0">
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${typeColors[item.type] || "bg-slate-100 text-slate-700"}`}>
            {item.type_label}
          </span>
          <User className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
          <span className="font-medium text-slate-800 truncate">{item.client_nom}</span>
          <Phone className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
          <span className="text-slate-500 text-xs">{item.client_telephone}</span>
        </div>
        <div className="flex items-center gap-1 text-xs text-slate-400">
          <Clock className="w-3 h-3" />
          <span>Livré le {item.livraison_date?.slice(0, 10)}</span>
        </div>
      </div>
      <Textarea value={message} onChange={(e) => setMessage(e.target.value)} className="text-sm mb-2 min-h-[70px]" data-testid={`sms-message-${item.id}`} />
      <div className="flex items-center gap-2 justify-end">
        <Button variant="outline" size="sm" onClick={markOnly} className="text-xs" data-testid={`sms-mark-${item.id}`}>
          <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Marquer envoyé
        </Button>
        <Button size="sm" onClick={handleSend} disabled={sending} className="bg-indigo-600 hover:bg-indigo-700 text-xs" data-testid={`sms-send-${item.id}`}>
          <Send className="w-3.5 h-3.5 mr-1" /> Envoyer SMS
        </Button>
      </div>
    </div>
  );
};

const EmailCard = ({ item, onSent }) => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [subject, setSubject] = useState(item.subject);
  const [body, setBody] = useState(item.body);
  const [sending, setSending] = useState(false);

  const send = async (openMail) => {
    setSending(true);
    if (openMail) {
      window.location.href = `mailto:${item.client_email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    }
    setTimeout(async () => {
      try {
        await axios.post(`${API}/communications/emails-mark-sent`, {
          livraison_id: item.livraison_id,
          type_email: item.type,
        }, { headers });
        toast.success("Marqué comme envoyé");
        onSent(item.id);
      } catch { toast.error("Erreur"); }
      finally { setSending(false); }
    }, openMail ? 500 : 0);
  };

  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-white" data-testid={`email-item-${item.id}`}>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2 text-sm min-w-0">
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${typeColors[item.type] || "bg-slate-100 text-slate-700"}`}>{item.type_label}</span>
          <User className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
          <span className="font-medium text-slate-800 truncate">{item.client_nom}</span>
          <Mail className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
          <span className="text-slate-500 text-xs truncate">{item.client_email}</span>
        </div>
        <div className="flex items-center gap-1 text-xs text-slate-400">
          <Clock className="w-3 h-3" />
          <span>Livré le {item.livraison_date?.slice(0, 10)}</span>
        </div>
      </div>
      <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="text-sm mb-2" placeholder="Sujet" data-testid={`email-subject-${item.id}`} />
      <Textarea value={body} onChange={(e) => setBody(e.target.value)} className="text-sm mb-2 min-h-[120px]" data-testid={`email-body-${item.id}`} />
      <div className="flex items-center gap-2 justify-end">
        <Button variant="outline" size="sm" onClick={() => send(false)} className="text-xs" data-testid={`email-mark-${item.id}`}>
          <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Marquer envoyé
        </Button>
        <Button size="sm" onClick={() => send(true)} disabled={sending} className="bg-indigo-600 hover:bg-indigo-700 text-xs" data-testid={`email-send-${item.id}`}>
          <Send className="w-3.5 h-3.5 mr-1" /> Envoyer Email
        </Button>
      </div>
    </div>
  );
};

// ---- Compose free email dialog ----
const ComposeEmailDialog = ({ open, onOpenChange }) => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [contacts, setContacts] = useState([]);
  const [selected, setSelected] = useState([]); // array of {email, nom}
  const [search, setSearch] = useState("");
  const [freeEmail, setFreeEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/communications/client-contacts`, { headers }).then((r) => {
      setContacts((r.data || []).filter((c) => c.email));
    }).catch(() => {});
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredContacts = useMemo(() => {
    if (!search.trim()) return contacts.slice(0, 50);
    const q = search.toLowerCase();
    return contacts.filter((c) =>
      (c.nom || "").toLowerCase().includes(q) || (c.email || "").toLowerCase().includes(q)
    ).slice(0, 50);
  }, [contacts, search]);

  const addContact = (c) => {
    if (selected.find((s) => s.email === c.email)) return;
    setSelected([...selected, { email: c.email, nom: c.nom }]);
  };

  const addFree = () => {
    const em = freeEmail.trim();
    if (!em || !em.includes("@")) { toast.error("Email invalide"); return; }
    if (selected.find((s) => s.email === em)) { setFreeEmail(""); return; }
    setSelected([...selected, { email: em, nom: em }]);
    setFreeEmail("");
  };

  const removeContact = (email) => setSelected(selected.filter((s) => s.email !== email));

  const handleSend = async () => {
    if (!selected.length) { toast.error("Ajoutez au moins un destinataire"); return; }
    if (!subject.trim() || !body.trim()) { toast.error("Sujet et message requis"); return; }
    setSending(true);
    // Log BEFORE mailto navigation to avoid SPA unload race
    try {
      await axios.post(`${API}/communications/emails-compose-log`, {
        recipients: selected.map((s) => s.email),
        subject, body,
      }, { headers });
    } catch { /* logged silently */ }
    const emails = selected.map((s) => s.email).join(",");
    window.open(`mailto:${emails}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, "_self");
    toast.success(`Email ouvert pour ${selected.length} destinataire(s)`);
    onOpenChange(false);
    setSelected([]); setSubject(""); setBody(""); setSearch(""); setFreeEmail("");
    setSending(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="compose-email-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-indigo-600" />
            Nouveau mail
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Selected chips */}
          {selected.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="compose-recipients">
              {selected.map((s) => (
                <span key={s.email} className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs">
                  {s.nom || s.email}
                  <button onClick={() => removeContact(s.email)} className="hover:text-indigo-900"><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
          )}

          {/* Free email input */}
          <div>
            <Label>Ajouter un destinataire libre</Label>
            <div className="flex gap-2 mt-1">
              <Input type="email" placeholder="email@exemple.com" value={freeEmail} onChange={(e) => setFreeEmail(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addFree(); } }} data-testid="compose-free-email" />
              <Button onClick={addFree} variant="outline" data-testid="compose-add-free-btn"><Plus className="w-4 h-4" /></Button>
            </div>
          </div>

          {/* Contact picker */}
          <div>
            <Label>Ou rechercher un client</Label>
            <Input placeholder="Rechercher par nom ou email..." value={search} onChange={(e) => setSearch(e.target.value)} className="mt-1" data-testid="compose-search" />
            <div className="mt-2 max-h-40 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
              {filteredContacts.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-3">Aucun client avec email</p>
              ) : filteredContacts.map((c) => (
                <button key={c.id} onClick={() => addContact(c)} className="w-full px-3 py-2 text-left hover:bg-indigo-50 text-sm flex justify-between" data-testid={`compose-contact-${c.id}`}>
                  <span className="font-medium text-slate-700">{c.nom}</span>
                  <span className="text-slate-400 text-xs">{c.email}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label>Sujet</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Sujet de l'email" data-testid="compose-subject" />
          </div>
          <div>
            <Label>Message</Label>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Votre message..." rows={6} data-testid="compose-body" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={handleSend} disabled={sending || !selected.length || !subject.trim() || !body.trim()} className="bg-indigo-600 hover:bg-indigo-700" data-testid="compose-send-btn">
            <SendHorizonal className="w-4 h-4 mr-2" />
            Ouvrir mon client mail ({selected.length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Communications = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTournee = searchParams.get("tournee");

  const [tab, setTab] = useState(initialTournee ? "fin" : "sms");
  const [smsPending, setSmsPending] = useState([]);
  const [emailsPending, setEmailsPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("all");
  const [liensAvis, setLiensAvis] = useState([]);
  const [avisLinkId, setAvisLinkId] = useState("");

  // End-of-tournée batch
  const [terminees, setTerminees] = useState([]);
  const [selectedTournee, setSelectedTournee] = useState(initialTournee || "");
  const [tourneeSms, setTourneeSms] = useState([]);
  const [tourneeLoading, setTourneeLoading] = useState(false);

  // Compose dialog
  const [composeOpen, setComposeOpen] = useState(false);

  const fetchPending = async () => {
    try {
      const q = avisLinkId ? `?lien_avis_id=${avisLinkId}` : "";
      const [sm, em, tt] = await Promise.all([
        axios.get(`${API}/communications/sms-pending${q}`, { headers }),
        axios.get(`${API}/communications/emails-pending${q}`, { headers }),
        axios.get(`${API}/communications/tournees-terminees`, { headers }),
      ]);
      setSmsPending(sm.data);
      setEmailsPending(em.data);
      setTerminees(tt.data);
    } catch { toast.error("Erreur chargement"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchPending(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Charge les liens d'avis (Facebook, Google, Instagram...) + le lien par défaut
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/marketing/liens-avis`, { headers }),
      axios.get(`${API}/parametres`, { headers }),
    ]).then(([la, pa]) => {
      const links = la.data || [];
      setLiensAvis(links);
      const def = pa.data?.lien_avis_defaut_id;
      if (def && links.some((l) => l.id === def)) setAvisLinkId(def);
      else if (links.length) setAvisLinkId(links.find((l) => /google/i.test((l.nom || "") + (l.plateforme || "")))?.id || links[0].id);
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAvisLinkChange = async (id) => {
    setAvisLinkId(id);
    try {
      await axios.post(`${API}/communications/set-avis-defaut`, { lien_avis_id: id }, { headers });
      const q = id ? `?lien_avis_id=${id}` : "";
      const [sm, em] = await Promise.all([
        axios.get(`${API}/communications/sms-pending${q}`, { headers }),
        axios.get(`${API}/communications/emails-pending${q}`, { headers }),
      ]);
      setSmsPending(sm.data);
      setEmailsPending(em.data);
      if (selectedTournee) {
        const r = await axios.get(`${API}/communications/tournee-sms/${selectedTournee}${q}`, { headers });
        setTourneeSms((r.data.items || []).filter((x) => !x.already_sent));
      }
      const chosen = liensAvis.find((l) => l.id === id);
      toast.success(`Lien d'avis : ${chosen?.nom || "mis à jour"}`);
    } catch { toast.error("Erreur changement de lien"); }
  };

  // When a tournée is selected (or comes from query param), fetch its SMS batch
  useEffect(() => {
    if (!selectedTournee) { setTourneeSms([]); return; }
    setTourneeLoading(true);
    const q = avisLinkId ? `?lien_avis_id=${avisLinkId}` : "";
    axios.get(`${API}/communications/tournee-sms/${selectedTournee}${q}`, { headers })
      .then((r) => {
        // Keep only not-yet-sent
        setTourneeSms((r.data.items || []).filter((x) => !x.already_sent));
      })
      .catch(() => toast.error("Erreur chargement tournée"))
      .finally(() => setTourneeLoading(false));
    // Sync URL
    setSearchParams(selectedTournee ? { tournee: selectedTournee } : {});
  }, [selectedTournee]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSent = (id) => {
    setSmsPending((prev) => prev.filter((x) => x.id !== id));
    setEmailsPending((prev) => prev.filter((x) => x.id !== id));
    setTourneeSms((prev) => prev.filter((x) => x.id !== id));
  };

  const smsFiltered = useMemo(() => smsPending.filter((x) => filterType === "all" || x.type === filterType), [smsPending, filterType]);
  const emailsFiltered = useMemo(() => emailsPending.filter((x) => filterType === "all" || x.type === filterType), [emailsPending, filterType]);

  const sendAllSms = async (items, markEndpoint = "sms-mark-sent", typeField = "type_sms") => {
    if (!items.length) return;
    if (!window.confirm(`Ouvrir ${items.length} SMS les uns après les autres ?`)) return;
    for (const item of items) {
      const body = encodeURIComponent(item.message);
      const phone = (item.client_telephone || "").replace(/\s+/g, "");
      window.open(`sms:${phone}?body=${body}`, "_blank");
      try {
        await axios.post(`${API}/communications/${markEndpoint}`, {
          livraison_id: item.livraison_id,
          [typeField]: item.type,
        }, { headers });
      } catch { /* ignore */ }
    }
    toast.success(`${items.length} SMS marqués comme envoyés`);
    fetchPending();
    if (selectedTournee) {
      const r = await axios.get(`${API}/communications/tournee-sms/${selectedTournee}`, { headers });
      setTourneeSms((r.data.items || []).filter((x) => !x.already_sent));
    }
  };

  const totalFin = tourneeSms.length;

  return (
    <div className="space-y-6" data-testid="communications-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Communications</h1>
          <p className="text-slate-500 mt-1">SMS et emails à envoyer après vos livraisons</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {liensAvis.length > 0 && (
            <div className="flex items-center gap-2" data-testid="avis-link-selector">
              <span className="text-sm text-slate-500 whitespace-nowrap">Lien d'avis :</span>
              <Select value={avisLinkId} onValueChange={handleAvisLinkChange}>
                <SelectTrigger className="w-48" data-testid="avis-link-select">
                  <SelectValue placeholder="Choisir…" />
                </SelectTrigger>
                <SelectContent>
                  {liensAvis.map((l) => (
                    <SelectItem key={l.id} value={l.id} data-testid={`avis-link-${l.id}`}>{l.nom || l.plateforme || l.url}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button onClick={() => setComposeOpen(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="compose-email-btn">
            <Mail className="w-4 h-4 mr-2" />
            Nouveau mail
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-slate-200 overflow-x-auto">
        <button onClick={() => setTab("fin")} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${tab === "fin" ? "border-purple-600 text-purple-600" : "border-transparent text-slate-500 hover:text-slate-700"}`} data-testid="tab-fin">
          <Route className="w-4 h-4 inline mr-1.5" />
          Fin de tournée{totalFin > 0 && ` (${totalFin})`}
        </button>
        <button onClick={() => setTab("sms")} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${tab === "sms" ? "border-indigo-600 text-indigo-600" : "border-transparent text-slate-500 hover:text-slate-700"}`} data-testid="tab-sms">
          <MessageSquare className="w-4 h-4 inline mr-1.5" />
          SMS ({smsPending.length})
        </button>
        <button onClick={() => setTab("emails")} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${tab === "emails" ? "border-indigo-600 text-indigo-600" : "border-transparent text-slate-500 hover:text-slate-700"}`} data-testid="tab-emails">
          <Mail className="w-4 h-4 inline mr-1.5" />
          Emails ({emailsPending.length})
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">Chargement…</div>
      ) : tab === "fin" ? (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 flex-wrap gap-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Route className="w-5 h-5 text-purple-600" />
              SMS de fin de tournée
            </CardTitle>
            <div className="flex items-center gap-2">
              <Select value={selectedTournee} onValueChange={setSelectedTournee}>
                <SelectTrigger className="w-72" data-testid="fin-tournee-select">
                  <SelectValue placeholder="Choisir une tournée terminée…" />
                </SelectTrigger>
                <SelectContent>
                  {terminees.length === 0 ? (
                    <div className="text-xs text-slate-400 p-2">Aucune tournée terminée</div>
                  ) : terminees.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.nom} — {t.nb_clients_telephone}/{t.nb_arrets} ({t.date})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {tourneeSms.length > 0 && (
                <Button onClick={() => sendAllSms(tourneeSms, "sms-mark-sent", "type_sms")} className="bg-purple-600 hover:bg-purple-700" data-testid="send-all-fin-btn">
                  <SendHorizonal className="w-4 h-4 mr-2" />
                  Tout envoyer
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {!selectedTournee ? (
              <div className="py-8 text-center text-slate-400">
                <Route className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">Sélectionnez une tournée terminée pour voir les SMS à envoyer</p>
              </div>
            ) : tourneeLoading ? (
              <div className="py-6 text-center text-slate-400">Chargement…</div>
            ) : tourneeSms.length === 0 ? (
              <div className="py-6 text-center text-slate-400">
                <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
                <p className="text-sm">Tous les SMS de cette tournée ont déjà été envoyés 🎉</p>
              </div>
            ) : (
              <div className="space-y-3">
                {tourneeSms.map((it) => <SmsCard key={it.id} item={it} onSent={onSent} />)}
              </div>
            )}
          </CardContent>
        </Card>
      ) : tab === "sms" ? (
        <>
          <div className="flex items-center gap-2 flex-wrap">
            {["all", "j1", "j7", "j30"].map((t) => (
              <button key={t} onClick={() => setFilterType(t)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filterType === t ? "bg-indigo-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-indigo-300"}`} data-testid={`filter-${t}`}>
                {t === "all" ? "Tous" : t === "j1" ? "J+1 Avis" : t === "j7" ? "J+7 Parrainage" : "J+30 Relance"}
              </button>
            ))}
          </div>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-indigo-600" />
                SMS à envoyer ({smsFiltered.length})
              </CardTitle>
              {smsFiltered.length > 0 && (
                <Button onClick={() => sendAllSms(smsFiltered)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="send-all-sms-btn">
                  <SendHorizonal className="w-4 h-4 mr-2" /> Tout envoyer
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {smsFiltered.length === 0 ? (
                <div className="py-8 text-center text-slate-400">
                  <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">Aucun SMS à envoyer aujourd'hui</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {smsFiltered.map((it) => <SmsCard key={it.id} item={it} onSent={onSent} />)}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      ) : (
        <>
          <div className="flex items-center gap-2 flex-wrap">
            {["all", "j1", "j7", "j30"].map((t) => (
              <button key={t} onClick={() => setFilterType(t)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filterType === t ? "bg-indigo-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-indigo-300"}`} data-testid={`filter-email-${t}`}>
                {t === "all" ? "Tous" : t === "j1" ? "J+1" : t === "j7" ? "J+7" : "J+30"}
              </button>
            ))}
          </div>
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Mail className="w-5 h-5 text-indigo-600" />
                Emails à envoyer ({emailsFiltered.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {emailsFiltered.length === 0 ? (
                <div className="py-8 text-center text-slate-400">
                  <Mail className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">Aucun email à envoyer aujourd'hui</p>
                  <p className="text-xs mt-1 text-slate-300">Seuls les clients ayant un email enregistré apparaissent ici</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {emailsFiltered.map((it) => <EmailCard key={it.id} item={it} onSent={onSent} />)}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      <ComposeEmailDialog open={composeOpen} onOpenChange={setComposeOpen} />
    </div>
  );
};

export default Communications;
